import {
  ModernSearchHit,
  SearchHit,
  SearchQuery,
  SearchResults,
  FetchError,
} from "frontend/types";
import { UseFetchProps, useFetch } from "./use-fetch";

type UseSearchArgs = SearchQuery | null | undefined;

const searchApiUrl = "/api/search/query";

export function createSearchParams(search: UseSearchArgs) {
  if (search?.query === undefined) return {};

  return {
    from: search?.from?.toString(),
    size: search?.size?.toString(),
    index: search.index,
    query: search.query,
    facets: search?.facets?.join(","),
    filters: search?.filters,
    sort: search?.sort,
    facetLimit: search?.facetLimit?.toString(),
    fields: search?.fields?.join(","),
    options: search?.options?.join(","),
  };
}

interface UseSearchOptions {
  dedupe?: boolean;
  enabled?: boolean;
  defaultSize?: number;
}

function getFetchErrorMessage(
  error: FetchError | undefined,
): string | undefined {
  if (!error) return undefined;
  if (error.status === 403) return "Access Denied";

  if (
    typeof error.info === "object" &&
    error.info !== null &&
    "error" in error.info
  ) {
    return String((error.info as { error: unknown }).error);
  }

  return `Search request failed (${error.status})`;
}

export function useSearch<T extends object = SearchHit>(
  search: UseSearchArgs,
  options: UseSearchOptions = {},
) {
  const { enabled = true } = options;
  const baseSize = search?.size ?? options.defaultSize ?? 25;
  const size = options.dedupe ? baseSize + 10 : baseSize;
  const fields = search?.fields;

  const fetchProps: UseFetchProps = {
    url: searchApiUrl,
    params: createSearchParams({ ...(search ?? {}), size, fields }),
  };

  const shouldFetch = Boolean(enabled && search?.query !== undefined);
  const swr = useFetch<SearchResults<T>, FetchError>(fetchProps, {
    enabled: shouldFetch,
  });

  const rawData = swr.data;
  const hasBodyError =
    rawData &&
    typeof rawData === "object" &&
    "error" in rawData &&
    rawData.error;

  const data =
    options.dedupe && rawData?.hits && !hasBodyError
      ? {
          ...rawData,
          hits: getDedupedHits({ hits: rawData.hits, search, options }),
        }
      : rawData;

  let searchError: string | undefined;

  if (swr.error) {
    searchError = getFetchErrorMessage(swr.error);
  } else if (hasBodyError) {
    searchError = String((rawData as { error?: unknown } | undefined)?.error);
  }
  return {
    ...swr,
    data,
    searchError,
  };
}

export function useMultiSearch<T extends object = ModernSearchHit>(
  search: Array<UseSearchArgs>,
  options: UseSearchOptions = {},
) {
  const fetchProps: UseFetchProps = {
    method: "POST",
    url: searchApiUrl,
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      queries: search.map((s) => {
        const baseSize = s?.size ?? options.defaultSize ?? 25;
        const size = options.dedupe ? baseSize + 10 : baseSize;
        return createSearchParams({ ...(s ?? {}), size });
      }),
    }),
  };

  const swr = useFetch<Array<SearchResults<T>>, FetchError>(fetchProps);

  const data =
    options.dedupe && swr.data
      ? swr.data.map((result, i) =>
          Array.isArray(result.hits)
            ? {
                ...result,
                hits: getDedupedHits<T>({
                  hits: result.hits,
                  search: search[i],
                  options,
                }),
              }
            : result,
        )
      : swr.data;

  let searchError: string | undefined;

  if (swr.error) {
    searchError = getFetchErrorMessage(swr.error);
  } else if (data) {
    const firstWithError = data.find(
      (r) =>
        r &&
        typeof r === "object" &&
        "error" in r &&
        (r as { error?: unknown }).error,
    );
    if (firstWithError) {
      searchError = String((firstWithError as { error?: unknown }).error);
    }
  }

  return {
    ...swr,
    data,
    searchError,
  };
}

interface GetDedupedHitsArgs<S extends object = SearchHit> {
  hits: Array<S>;
  search: UseSearchArgs;
  options: UseSearchOptions;
}

function getDedupedHits<S extends object = SearchHit>({
  hits,
  search,
  options,
}: GetDedupedHitsArgs<S>): Array<S> {
  const deduped: Array<S> = [];
  const size = search?.size ?? options.defaultSize ?? 25;

  for (const hit of hits) {
    const exists = deduped.some((item) => {
      if ("id" in item && "id" in hit && item.id === hit.id) return true;
      if (
        "HBSTitle" in item &&
        "HBSTitle" in hit &&
        item.HBSTitle === hit.HBSTitle
      ) {
        return true;
      }
      return false;
    });

    if (!exists) deduped.push(hit);
    if (deduped.length >= size) break;
  }

  return deduped;
}
